import { useState } from 'react';
import { CONTACT_EMAIL } from '../lib/contact';
import { Button, Dialog, DialogBody, DialogFooter, Field, Textarea } from './ui';

export interface CodeDialogProps {
  open: boolean;
  onClose: () => void;
  /** Redeems the code typed; true if it was valid, and the dialog closes. */
  onRedeem: (code: string) => Promise<boolean>;
  /** Called after a valid code, once the dialog has closed. */
  onRedeemed?: () => void;
}

const MAIL_SUBJECT = 'Code for a Chores Champ account';

/** Where a code is asked for and taken: says how to get one, and checks the one pasted in. */
export function CodeDialog({ open, onClose, onRedeem, onRedeemed }: CodeDialogProps) {
  const [code, setCode] = useState('');
  const [wrong, setWrong] = useState(false);
  const [checking, setChecking] = useState(false);

  const close = () => {
    setCode('');
    setWrong(false);
    onClose();
  };

  const redeem = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!code.trim() || checking) return;
    setChecking(true);
    let ok: boolean;
    try {
      ok = await onRedeem(code);
    } finally {
      setChecking(false);
    }
    setWrong(!ok);
    if (ok) {
      close();
      onRedeemed?.();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={close}
      title="Enter your code"
      description="Creating a Chores Champ account takes a code. Write to the address below to ask for one, then paste it here."
    >
      <form onSubmit={redeem} className="flex min-h-0 flex-1 flex-col">
        <DialogBody>
          <a
            href={`mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(MAIL_SUBJECT)}`}
            className="text-sm font-medium text-indigo-600 hover:text-indigo-500 dark:text-indigo-400 dark:hover:text-indigo-300"
          >
            {CONTACT_EMAIL}
          </a>

          <Field
            label="Code"
            htmlFor="licenceCode"
            error={wrong ? "That code doesn't work" : undefined}
            hint="Paste the whole code."
          >
            <Textarea
              id="licenceCode"
              value={code}
              onChange={(e) => {
                setCode(e.target.value);
                setWrong(false);
              }}
              invalid={wrong}
              autoComplete="off"
              spellCheck={false}
            />
          </Field>
        </DialogBody>

        <DialogFooter>
          <Button variant="ghost" onClick={close}>Cancel</Button>
          <Button type="submit" variant="primary" loading={checking} disabled={!code.trim()}>Redeem</Button>
        </DialogFooter>
      </form>
    </Dialog>
  );
}
